import {
    AlertColor,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    MenuItem,
    Modal,
    TextField,
    Theme,
    Typography,
    useMediaQuery,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { globalTheme } from "../../../common/theme";
import createStyles from "@mui/styles/createStyles";
import axios from "axios";
import React, { useImperativeHandle, useMemo } from "react";
import { SnackBarMessage, SnackBarMessageProps } from "../../component/SnackBarMessage";
import Title from "../../component/Title";
import { DropDownComboOption } from "../../component/GenericFilter";
import { ProductInfo, ProductSubCategory } from "./ProductAccessories";
import { renewToken } from "../../../services/AuthService";
import { OpenMode } from "../../../common/Enums";
import { GeneralDialog } from "../../component/GeneralDialog";
import { Categories, ProductRegions, SubCategories } from "../../../common/Constants";
import NumberFormat from "react-number-format";
import { ValidateNumber } from "../../../common/CommonFunctions";
import { NumberTypes } from "../../../models/enums/NumberType";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
    })
);

export interface ProductProps {
    onClose: Function;
}

export interface ProductSingleHandle {
    Initialise: (mode: OpenMode, product: ProductInfo) => void;
}

export const ProductSingle = React.forwardRef(
    (props: ProductProps, ref: React.Ref<ProductSingleHandle>) => {
        const style = { ...useStyles(), ...globalTheme() };

        const tinyScreen = useMediaQuery('(max-width:1366px)');
        const smallScreen = useMediaQuery('(max-width:1920px)');
        const normalScreen = useMediaQuery('(max-width:2560px)');

        const [open, setOpen] = React.useState(false);
        const [openMode, setOpenMode] = React.useState<OpenMode>(OpenMode.NEW);
        const [loading, setLoading] = React.useState<boolean>(false);
        const [hasError, setHasError] = React.useState(false);
        const [product, setProduct] = React.useState<ProductInfo>();
        const [originalProduct, setOriginalProduct] = React.useState<ProductInfo>();
        const [saved, setSaved] = React.useState<boolean>(false);

        const [subCategoryOptions, setSubCategoryOptions] = React.useState<ProductSubCategory[]>([]);

        const [priceErrorText, setPriceErrorText] = React.useState<string>("");
        const [poPercentErrorText, setPoPercentErrorText] = React.useState<string>("");
        const [quotationPercentErrorText, setQuotationPercentErrorText] = React.useState<string>("");
        const [netWeightErrorText, setNetWeightErrorText] = React.useState<string>("");

        //Dialogs and snacks
        const hasChanges = React.useRef<boolean>(false);
        const [dialogOpen, setDialogOpen] = React.useState<boolean>(false);
        const [snackSettings, setSnackSettings] =
        React.useState<SnackBarMessageProps>({
            open: false,
            message: "",
            subMessage: "",
            severity: undefined,
            onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
        });

        function showSnackBar(message: string, subMessage: string, colour?: AlertColor) {
            setSnackSettings({
                open: true,
                message: message,
                subMessage: subMessage,
                severity: colour,
                onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
            });
        }

        useImperativeHandle(ref, () => ({
            Initialise(mode: OpenMode, product: ProductInfo) {
                setOpen(true);
                setSaved(false);
                renewToken();
        
                let subCategories = SubCategories.filter(x => x.category==(product.category??"MACHINE"));
                setSubCategoryOptions(subCategories);
                setOriginalProduct(product);
                setProduct(product);

                if (mode == OpenMode.EDIT)
                    hasChanges.current = false;
                else
                    hasChanges.current = true; 

                setOpenMode(mode);
            },
        }));

        function closeModal() {
            setDialogOpen(false);
            setOpen(false);
            props.onClose(saved, originalProduct);
        }

        function closeModalCheck(keepOpen: boolean) {
            if (hasChanges.current) {
              setDialogOpen(true);
            } else {
              setDialogOpen(false);
              setOpen(keepOpen);
              props.onClose(saved, originalProduct);
            }
        }

        function OnProductChanged(
            propertyName: string,
            propertyValue: any,
            validationRegex?: RegExp
        ) {
            let proceed = true;
        
            if (validationRegex !== undefined) {
                proceed = validationRegex.test(propertyValue);
            }
        
            if (proceed) {
                let temp: any = { ...product };
        
                if (temp[propertyName] != propertyValue)
                {
                    hasChanges.current = true;
                    temp[propertyName] = propertyValue ?? null;
                    setProduct(temp);
                }
            }
        }
 
        function OnProductCateogryChanged(category: string) {
            setSubCategoryOptions(SubCategories.filter(x => x.category == category))
            let temp: any = { ...product };
            temp.category = category;
            temp.subCategory = SubCategories.find(x => x.category == category)?.subCategory;
            setProduct(temp);
        }
       
        function SaveProduct(close: boolean) {
            if (hasChanges.current)
            {
                if (product)
                {
                    if (product.model == null || product.model == '')
                    {
                        showSnackBar("Please enter a model for the product.", "", "error");
                        return;
                    }

                    if (product.productCode == null || product.productCode == '')
                    {
                        showSnackBar("Please enter a product code for the product.", "", "error");
                        return;
                    }

                    let p = {...product};
                    p.nameJpn = product.nameJpn ?? '';
                    p.nameChi = product.nameChi ?? '';
                    p.nameEng = product.nameEng ?? '';
                    p.modelName = product.modelName ?? '';
                    p.newCDCode = product.newCDCode ?? '';
                    p.jCat = product.jCat ?? '';
                    p.jSubCat = product.jSubCat ?? '';
                    p.modelCat = product.modelCat ?? '';
                    p.remark = product.remark ?? '';
                    p.description = product.description ?? '';
                    p.scaw = product.scaw ?? '';
    
                    if (openMode === OpenMode.EDIT)
                    {
                        axios.put<ProductInfo>('Api/V1/Product/', p).then(
                            (result) => {
                                showSnackBar("Product saved successfully.", "", "success");
                                hasChanges.current = false;
                                setSaved(true);
                                setProduct(result.data);
                                setOriginalProduct(result.data);
                                setOpen(!close);
                                setDialogOpen(false);
                                if (close)
                                    props.onClose(true, result.data);
                            },
                            (error) => {
                                showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
                            }
                        );
                    } 
                    else
                    {
                        axios.post<ProductInfo>('Api/V1/Product/', p).then(
                            (result) => {
                                showSnackBar("Product created successfully.", "", "success");
                                hasChanges.current = false;
                                setSaved(true);
                                setProduct(result.data);
                                setOriginalProduct(result.data);
                                setOpenMode(OpenMode.EDIT);
                                setOpen(!close);
                                setDialogOpen(false);
                                if (close)
                                    props.onClose(true, result.data);
                            },
                            (error) => {
                                showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
                            }
                        );
                    }
                }
            }
        }

        return (
            <div>
                <Modal open={open} onClose={() => closeModalCheck(true)}>
                    <div className={style.ModalMain}>
                        {!loading ? (
                            <div className={style.ModalBody}>
                                <Title pageTitle="Product" />

                                <div className={style.ModalHeading}>
                                    <Typography variant={ tinyScreen ? "h6" : (smallScreen ? "h5" : "h4") }>Product</Typography>
                                </div>

                                <div className={style.ModalContent}>
                                    <div className={style.ModalFields}>
                                        <TextField className={style.ModalContentField}
                                            label="Product Code"
                                            value = {product?.productCode ?? ""}
                                            onChange={(e) => OnProductChanged("productCode", e.target.value.slice(0, 30))}
                                            size="small"
                                        />
                                        <TextField className={style.ModalContentField}
                                            label="Name (中国語)"
                                            value = {product?.nameChi ?? ""}
                                            onChange={(e) => OnProductChanged("nameChi", e.target.value.slice(0, 50))}
                                            size="small"
                                        />
                                        <TextField className={style.ModalContentField}
                                            label="Name (日本語)"
                                            value = {product?.nameJpn ?? ""}
                                            onChange={(e) => OnProductChanged("nameJpn", e.target.value.slice(0, 50))}
                                            size="small"
                                        />
                                        <TextField className={style.ModalContentField}
                                            label="Name (英語)"
                                            value = {product?.nameEng ?? ""}
                                            onChange={(e) => OnProductChanged("nameEng", e.target.value.slice(0, 50))}
                                            size="small"
                                        />
                                    </div>
                                    <div className={style.ModalFields}>
                                        <TextField className={style.ModalContentField}
                                            label="Model"
                                            value = {product?.model ?? ""}
                                            onChange={(e) => OnProductChanged("model", e.target.value.slice(0, 50))}
                                            size="small"
                                        />
                                        <TextField className={style.ModalContentField}
                                            label="品目CD"
                                            value = {product?.newCDCode ?? ""}
                                            onChange={(e) => OnProductChanged("newCDCode", e.target.value.slice(0, 30))}
                                            size="small"
                                        />
                                        <TextField className={style.ModalContentField}
                                            label="Model Name"
                                            value = {product?.modelName ?? ""}
                                            onChange={(e) => OnProductChanged("modelName", e.target.value.slice(0, 50))}
                                            size="small"
                                        />
                                        <TextField className={style.ModalContentField}
                                            label="Model Category"
                                            value = {product?.modelCat ?? ""}
                                            onChange={(e) => OnProductChanged("modelCat", e.target.value.slice(0, 10))}
                                            size="small"
                                        />
                                    </div>
                                    <div className={style.ModalFields}>
                                        <TextField className={style.ModalContentFieldHalfWidth}
                                            label="Description"
                                            value = {product?.description ?? ""}
                                            onChange={(e) => OnProductChanged("description", e.target.value.slice(0, 30))}
                                            size="small"
                                        />
                                        <TextField className={style.ModalContentFieldHalfWidth}
                                            label="Remark"
                                            value = {product?.remark ?? ""}
                                            onChange={(e) => OnProductChanged("remark", e.target.value.slice(0, 50))}
                                            size="small"
                                        />
                                    </div>
                                    <div className={style.ModalFields}>
                                        <div className={style.ModalContentField}>
                                            <NumberFormat className={`${style.ModalInnerContentFieldOneThirdWidthLeft}`} 
                                                label="Price"
                                                customInput={TextField}
                                                value = {product?.price ?? 0}
                                                onValueChange={(e) => {
                                                    if (e.value !== "") {
                                                      let msg = ValidateNumber(e.value, NumberTypes.FLOAT, true);
                                                      setPriceErrorText(msg);
                                                      if (msg === "")
                                                        OnProductChanged("price", e.value);
                                                    }
                                                  }}                      
                                                error={priceErrorText !== ""}
                                                helperText={priceErrorText}
                                                thousandSeparator
                                                fullWidth
                                                size="small"
                                            />                                            
                                            <TextField className={style.ModalInnerContentFieldOneThirdWidthMiddle}
                                                label="Unit"
                                                value = {product?.unit ?? ""}
                                                onChange={(e) => OnProductChanged("unit", e.target.value.slice(0, 10))}
                                                size="small"
                                            />
                                            <TextField className={style.ModalInnerContentFieldOneThirdWidthRight}
                                                label="SPQ"
                                                value = {product?.minOrderQty ?? 1}
                                                onChange={(e) => OnProductChanged("minOrderQty", e.target.value)}
                                                size="small"
                                            />
                                        </div>
                                        <div className={style.ModalContentField}>
                                            <TextField className={style.ModalInnerContentFieldOneThirdWidthLeft}
                                                value={product?.category ?? Categories[0]}
                                                onChange={(e) => OnProductCateogryChanged(e.target.value)}
                                                select
                                                label="Category"
                                                size="small"
                                            >
                                                {Categories.map((item) => (
                                                    <MenuItem key={item} value={item}>
                                                    {item}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                            <TextField className={style.ModalInnerContentFieldTwoThirdWidthRight}
                                                label="Sub Category"
                                                value = {product?.subCategory ?? SubCategories[4].subCategory}
                                                select
                                                onChange={(e) => OnProductChanged("subCategory", e.target.value)}
                                                size="small"
                                            >
                                                {subCategoryOptions.map((item) => (
                                                    <MenuItem key={item.subCategory} value={item.subCategory}>
                                                        {item.subCategory}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </div>
                                        <TextField className={style.ModalContentField}
                                                label="Category Code"
                                                value = {product?.regionCode ?? ProductRegions[0]}
                                                select
                                                onChange={(e) => OnProductChanged("regionCode", e.target.value)}
                                                size="small"
                                        >
                                                {ProductRegions.map((item) => (
                                                    <MenuItem key={item.id} value={item.id}>
                                                        {item.value}
                                                    </MenuItem>
                                                ))}
                                        </TextField>
                                        <TextField className={style.ModalContentField}
                                            label="SCAW"
                                            value = {product?.scaw ?? ""}
                                            onChange={(e) => OnProductChanged("scaw", e.target.value.slice(0, 12))}
                                            size="small"
                                        />
                                    </div>
                                    <div className={style.ModalFields}>
                                        <div className={style.ModalContentField}>
                                            <NumberFormat className={`${style.ModalInnerContentFieldLeft}`} 
                                                label="Min PO Percent"
                                                customInput={TextField}
                                                value = {product?.minPOPercent ?? 55}
                                                onValueChange={(e) => {
                                                    if (e.value !== "") {
                                                      let msg = ValidateNumber(e.value, NumberTypes.FLOAT, true);
                                                      setPoPercentErrorText(msg);
                                                      if (msg === "")
                                                        OnProductChanged("minPOPercent", e.value);
                                                    }
                                                  }}                      
                                                error={poPercentErrorText !== ""}
                                                helperText={poPercentErrorText}
                                                decimalScale={1}
                                                thousandSeparator
                                                fullWidth
                                                size="small"
                                            />
                                            <NumberFormat className={`${style.ModalInnerContentFieldRight}`} 
                                                label="Min Quotation Percent"
                                                customInput={TextField}
                                                value = {product?.minQuotationPercent ?? 70}
                                                onValueChange={(e) => {
                                                    if (e.value !== "") {
                                                      let msg = ValidateNumber(e.value, NumberTypes.FLOAT, true);
                                                      setQuotationPercentErrorText(msg);
                                                      if (msg === "")
                                                        OnProductChanged("minQuotationPercent", e.value);
                                                    }
                                                  }}                      
                                                error={quotationPercentErrorText !== ""}
                                                helperText={quotationPercentErrorText}
                                                decimalScale={1}
                                                thousandSeparator
                                                fullWidth
                                                size="small"
                                            />                                            
                                        </div>
                                        <div className={style.ModalContentField}>
                                            <TextField className={style.ModalInnerContentFieldLeft}
                                                label="J. Category"
                                                value = {product?.jCat ?? ""}
                                                onChange={(e) => OnProductChanged("jCat", e.target.value.slice(0, 10))}
                                                size="small"
                                            />
                                            <TextField className={style.ModalInnerContentFieldRight}
                                                label="J. Sub Category"
                                                value = {product?.jSubCat ?? ""}
                                                onChange={(e) => OnProductChanged("jSubCat", e.target.value.slice(0, 2))}
                                                size="small"
                                            />
                                        </div>
                                        <NumberFormat className={`${style.ModalContentField}`} 
                                            label="Net Weight (KG)"
                                            customInput={TextField}
                                            value = {product?.netWeight ?? 0}
                                            onValueChange={(e) => {
                                                if (e.value !== "") {
                                                    let msg = ValidateNumber(e.value, NumberTypes.FLOAT, true);
                                                    setNetWeightErrorText(msg);
                                                    if (msg === "")
                                                        OnProductChanged("netWeight", e.value);
                                                }
                                            }}                      
                                            error={netWeightErrorText !== ""}
                                            helperText={netWeightErrorText}
                                            decimalScale={4}
                                            thousandSeparator
                                            fullWidth
                                            size="small"
                                        />      
                                        <TextField className={style.ModalContentField}
                                            label="Last Updated By"
                                            value = {product?.staff ?? ""}
                                            inputProps={{ readOnly: true }}
                                            size="small"
                                        />
                                    </div>
                                </div>
                                <div className={style.ModalFooter}>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        className={style.margin}
                                        onClick={(e) => {
                                                SaveProduct(false);
                                            }
                                        }
                                        size="small"
                                    >
                                        Save
                                    </Button>
                                    <div style={{ width: `100%`, minWidth: "10" }}></div>
                                    <Button
                                        variant="contained"
                                        className={style.margin}
                                        onClick={(e) => closeModalCheck(false)}
                                        size="small"
                                    >
                                        Close
                                    </Button>                                
                                </div>
                            </div>
                        ) : (
                            <div style={{ textAlign: "center" }}>
                                <CircularProgress color="primary" />
                            </div>
                        )}
                    </div>
                </Modal>

                <SnackBarMessage {...snackSettings} />

                <GeneralDialog
                    title={"Save Changes?"}
                    message={
                        "Changes have been made, did you want to save before closing?"
                    }
                    open={dialogOpen}
                    options={[
                        {
                        text: "Save",
                        action: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => SaveProduct(true),
                        color: "secondary",
                        variant: "contained",
                        },
                        {
                        text: "No",
                        action: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => closeModal(),
                        color: "primary",
                        variant: "contained",
                        },
                    ]}
                />

            </div>
        );


    }

);


